import { useEffect, useMemo, useState } from "react";
import { Ban, Check, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { type BlockRow, type MatchMode, handleControls, previewHandleBlock } from "@/lib/handleControls";
import { formatDate } from "./shared";

type BlockDraft = { id?: string; pattern: string; match_mode: MatchMode; note: string };
function emptyBlockDraft(): BlockDraft { return { pattern: "", match_mode: "contains", note: "" }; }

export function BlocksPanel() {
  const [rows, setRows] = useState<BlockRow[]>([]);
  const [userLabels, setUserLabels] = useState<Record<string, { email: string | null }>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState<BlockDraft>(emptyBlockDraft());
  const [saving, setSaving] = useState(false);
  const [previewHandle, setPreviewHandle] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const data = await handleControls.block.list({ search: search || undefined });
      setRows(data.rows); setUserLabels(data.user_labels);
    } catch (e) { toast.error((e as Error).message); }
    finally { setLoading(false); }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, []);

  const previewHit = useMemo(() => previewHandleBlock(previewHandle, rows), [previewHandle, rows]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        pattern: draft.pattern.trim().toLowerCase(),
        match_mode: draft.match_mode,
        note: draft.note.trim() || null,
      };
      if (draft.id) await handleControls.block.update({ id: draft.id, ...payload });
      else await handleControls.block.create(payload);
      toast.success("Saved"); setDialogOpen(false); await load();
    } catch (e) { toast.error((e as Error).message); }
    finally { setSaving(false); }
  };

  const release = async (r: BlockRow) => {
    if (!confirm(`Remove block for "${r.pattern}" (${r.match_mode})?`)) return;
    try { await handleControls.block.release(r.id); toast.success("Removed"); await load(); }
    catch (e) { toast.error((e as Error).message); }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2"><Ban className="h-4 w-4" /> Blocked substrings</CardTitle>
          <Button size="sm" onClick={() => { setDraft(emptyBlockDraft()); setDialogOpen(true); }}>
            <Plus className="h-4 w-4" /> Block a pattern
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="rounded-md border bg-muted/30 p-3">
          <Label className="text-xs">Live preview</Label>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <Input
              placeholder="Type a candidate handle, e.g. excerptus123"
              value={previewHandle}
              onChange={(e) => setPreviewHandle(e.target.value)}
              className="max-w-sm"
            />
            {previewHandle.trim() && (
              previewHit
                ? <Badge variant="destructive">Blocked by "{previewHit.pattern}" ({previewHit.match_mode})</Badge>
                : <Badge variant="secondary">No block matches</Badge>
            )}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Allocation overrides aren't shown here — anyone with an allocated reservation for the exact handle can still claim it.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Input placeholder="Search pattern…" value={search} onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()} className="w-56" />
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Refresh"}
          </Button>
        </div>

        <div className="overflow-x-auto rounded-md border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">Pattern</th>
                <th className="px-3 py-2">Match</th>
                <th className="px-3 py-2">Note</th>
                <th className="px-3 py-2">Created by</th>
                <th className="px-3 py-2">Created</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-muted-foreground">No blocked patterns yet.</td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.id} className="border-t">
                  <td className="px-3 py-2 font-mono">{r.pattern}</td>
                  <td className="px-3 py-2"><Badge variant="outline">{r.match_mode}</Badge></td>
                  <td className="px-3 py-2 text-xs text-muted-foreground max-w-[280px] truncate">{r.note ?? "—"}</td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">
                    {r.created_by ? userLabels[r.created_by]?.email ?? r.created_by.slice(0, 8) : "—"}
                  </td>
                  <td className="px-3 py-2 text-xs text-muted-foreground">{formatDate(r.created_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <Button variant="ghost" size="icon" onClick={() => { setDraft({ id: r.id, pattern: r.pattern, match_mode: r.match_mode, note: r.note ?? "" }); setDialogOpen(true); }} aria-label="Edit">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => release(r)} aria-label="Remove">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{draft.id ? `Edit block` : "Block a pattern"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Pattern</Label>
              <Input value={draft.pattern} onChange={(e) => setDraft({ ...draft, pattern: e.target.value })} placeholder="excerptus" />
              <p className="mt-1 text-xs text-muted-foreground">2–24 chars, lowercase a–z and 0–9.</p>
            </div>
            <div>
              <Label>Match mode</Label>
              <Select value={draft.match_mode} onValueChange={(v) => setDraft({ ...draft, match_mode: v as MatchMode })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="contains">Contains (e.g. blocks excerptus123, myexcerptus)</SelectItem>
                  <SelectItem value="exact">Exact match only</SelectItem>
                  <SelectItem value="prefix">Starts with pattern</SelectItem>
                  <SelectItem value="suffix">Ends with pattern</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Note (optional)</Label>
              <Input value={draft.note} onChange={(e) => setDraft({ ...draft, note: e.target.value })} placeholder="Why this is blocked" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)}><X className="h-4 w-4" /> Cancel</Button>
            <Button onClick={submit} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
